import type { ImageSource } from './types';

import { IMAGE_TYPE, SPECIAL_MATERIAL_IMAGE, srcSizes } from './constants';
import type {
  HoverImageType,
  PrimaryImageType,
} from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import { removeAiWatermark } from '~/utilities/helpers/shouldShowAiIcon';

export const hasSpecialImage = (materialGroup: string): boolean =>
  SPECIAL_MATERIAL_IMAGE.includes(materialGroup);

type ResolveImageProfilesParams = {
  imageType: PrimaryImageType | HoverImageType;
  primaryProfiles?: string | null;
  hoverProfiles?: string | null;
  flatProfiles?: string | null;
};

/**
 * Maps a content-selected image view to a profile priority string. Callers
 * supply the profiles for each view; the `flat` mapping differs per site
 * (G-Star uses dedicated flat profiles, AAF reuses its primary profiles).
 */
export const resolveImageProfiles = ({
  imageType,
  primaryProfiles,
  hoverProfiles,
  flatProfiles,
}: ResolveImageProfilesParams): string | null | undefined => {
  if (imageType === IMAGE_TYPE.flat) {
    return flatProfiles;
  }

  if (imageType === IMAGE_TYPE.defaultHover) {
    return hoverProfiles;
  }

  return primaryProfiles;
};

export const makeImageUrl = (imageUrl: string, imageParams: string): string =>
  imageUrl.replace('{{dimensions}}', imageParams);

export const buildImageSource = (
  url?: string | null,
  baseImageUrl?: string,
  aspectRatio = 0.75,
  isAI = false
): ImageSource | null => {
  if (!url) {
    return null;
  }

  let fullUrl = `${baseImageUrl ?? ''}${url}`;

  if (isAI) {
    fullUrl = removeAiWatermark(fullUrl) ?? '';
  }

  const src = makeImageUrl(fullUrl, `w_1000,ar_${aspectRatio},q_auto`);
  const srcSet = srcSizes
    .map(size => `${makeImageUrl(fullUrl, `w_${size},ar_${aspectRatio},q_auto`)} ${size}w`)
    .join(', ');

  return { src, srcSet, isAI };
};
